import { useCallback, useEffect } from "react";
import { create } from "zustand";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { getUserEnrollments } from "services";
import { Session } from "utils";

dayjs.extend(isSameOrBefore);

/**
 * Global store that tracks which batches the logged-in user has lost access to
 * (either explicitly removed or access expired) so that a global announcement
 * banner can be shown on every route for the currently selected batch.
 *
 * The selected batch is shared across the app via sessionStorage("selectedBatch").
 */
const useAnnouncementStore = create((set, get) => ({
  loaded: false,
  loading: false,
  // { [batchId]: batchName } for every batch the user no longer has access to
  removedBatches: {},
  selectedBatchId:
    typeof window !== "undefined"
      ? sessionStorage.getItem("selectedBatch")
      : null,
  // { [batchId]: true } batches whose banner the user has dismissed this session
  dismissed: {},

  setSelectedBatchId: (selectedBatchId) => set({ selectedBatchId }),

  setRemovedBatches: (removedBatches) =>
    set({ removedBatches: removedBatches || {}, loaded: true }),

  dismiss: (batchId) =>
    set((state) => ({
      dismissed: { ...state.dismissed, [batchId]: true },
    })),

  /**
   * Fetch the user's enrollments once and derive the set of batches without
   * access. Idempotent: skipped if already loaded/loading unless `force`.
   */
  loadEnrollments: async (force = false) => {
    const { loaded, loading } = get();
    if ((loaded || loading) && !force) return;
    if (!Session.userId) return;

    set({ loading: true });
    try {
      const { batchUserMappings = [] } = await getUserEnrollments();

      const removedBatches = {};
      batchUserMappings.forEach((bum) => {
        const isRemoved =
          bum.removed ||
          dayjs(bum?.accessExpiresOn).isSameOrBefore(dayjs()) ||
          false;

        if (isRemoved && bum.batch?._id) {
          removedBatches[bum.batch._id] = bum.batch?.name || "this batch";
        }
      });

      set({ removedBatches, loaded: true });
    } catch (error) {
      console.error("Unable to load batch access info", error);
    } finally {
      set({ loading: false });
    }
  },

  reset: () =>
    set({
      loaded: false,
      loading: false,
      removedBatches: {},
      dismissed: {},
    }),
}));

/**
 * Returns whether the user has lost access to the currently selected batch.
 * Use it to disable feature CTAs (Interview Wizard, Shortlisting Genie, ...)
 * for batches the user has been removed from. Triggers a one-time enrollment
 * load so it works on routes that don't mount SelectBatch.
 */
export const useSelectedBatchRemoved = () => {
  const selectedBatchId = useAnnouncementStore((s) => s.selectedBatchId);
  const removedBatches = useAnnouncementStore((s) => s.removedBatches);
  const loadEnrollments = useAnnouncementStore((s) => s.loadEnrollments);

  useEffect(() => {
    loadEnrollments();
  }, [loadEnrollments]);

  return Boolean(selectedBatchId && removedBatches[selectedBatchId]);
};

/**
 * Returns a predicate `(batchId) => boolean` telling whether the user has lost
 * access to a specific batch. Useful where items span multiple batches (e.g.
 * shortlisting tasks) and must be gated per-item rather than by the selected
 * batch. Unknown/missing batch ids are treated as "has access" (returns false).
 */
export const useIsBatchRemoved = () => {
  const removedBatches = useAnnouncementStore((s) => s.removedBatches);
  const loadEnrollments = useAnnouncementStore((s) => s.loadEnrollments);

  useEffect(() => {
    loadEnrollments();
  }, [loadEnrollments]);

  return useCallback(
    (batchId) => Boolean(batchId && removedBatches[batchId]),
    [removedBatches]
  );
};

export default useAnnouncementStore;
